/**
 * backdrop.ts — procedural backdrop layer (v2M7.8a/b).
 *
 * Owns the 12 backdrop layers from GRAPHICS §10.1 — galaxy starfield,
 * system starfield, and main-menu key art (the nebula behind the title
 * lockup). Per §10.2's procedural-not-AI lock: every layer is generated
 * in code at app boot, cached as an OffscreenCanvas, and blitted by
 * its caller. The same load-and-blit runtime contract carries forward
 * to v3M11 polish — when commissioned PNG art lands, getStarfield /
 * getMainMenuKeyArt swap their procedural body for an HTMLImageElement
 * load, and no caller changes.
 *
 * Headless-safe: when OffscreenCanvas is undefined (vitest 'node' env),
 * accessors return null and callers fall back to the prior flat-fill
 * backdrop. No DOM stub required.
 *
 * Determinism: every generator seeds a Mulberry32 PRNG from a string
 * hash, so the same `seed` always produces an identical bitmap. Useful
 * when QA captures a screenshot in one run and diffs against another.
 */

const STARFIELD_SIZE = 1024;
const KEY_ART_W = 1920;
const KEY_ART_H = 1080;

const starfieldCache = new Map<string, OffscreenCanvas | null>();
let keyArtCache: OffscreenCanvas | null | undefined;

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 0x100000000;
  };
}

function fnv1a(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

/**
 * Get (or generate-and-cache) a tileable starfield bitmap for the given
 * seed. Galaxy and system view pass distinct seeds per §10.1 so their
 * star layouts don't read as identical. Returns null when OffscreenCanvas
 * is unavailable (test env) — caller should fall back to a flat fill.
 */
export function getStarfield(seed: string): OffscreenCanvas | null {
  if (starfieldCache.has(seed)) return starfieldCache.get(seed) ?? null;
  if (typeof OffscreenCanvas === 'undefined') {
    starfieldCache.set(seed, null);
    return null;
  }
  const canvas = new OffscreenCanvas(STARFIELD_SIZE, STARFIELD_SIZE);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    starfieldCache.set(seed, null);
    return null;
  }
  paintStarfield(ctx, mulberry32(fnv1a(seed)));
  starfieldCache.set(seed, canvas);
  return canvas;
}

/**
 * Paint a tileable starfield. Three populations of star sizes / brightnesses:
 *   - tiny dim background dust (~600 stars, 1 px, low alpha)
 *   - mid-distance brighter stars (~140 stars, 1–2 px, medium alpha)
 *   - foreground bright accents (~25 stars, 2–3 px with a 1 px halo)
 *
 * Plus a few drifty "nebulae" — large dim coloured radial blobs — to add
 * faint colour variance so the field doesn't read as flat black.
 */
function paintStarfield(ctx: OffscreenCanvasRenderingContext2D, rng: () => number): void {
  // Faint nebulae behind the stars — mostly desaturated cool tones.
  const nebulaTints = [
    'rgba(80, 110, 160, 0.10)',
    'rgba(120, 90, 160, 0.08)',
    'rgba(60, 120, 140, 0.07)',
    'rgba(140, 100, 110, 0.06)',
  ];
  for (let i = 0; i < 5; i++) {
    const cx = rng() * STARFIELD_SIZE;
    const cy = rng() * STARFIELD_SIZE;
    const r = 80 + rng() * 220;
    const tint = nebulaTints[Math.floor(rng() * nebulaTints.length)];
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, tint);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Dust population — densest, dimmest. 1 px fillRect for speed.
  for (let i = 0; i < 600; i++) {
    const x = rng() * STARFIELD_SIZE;
    const y = rng() * STARFIELD_SIZE;
    const a = 0.1 + rng() * 0.15;
    ctx.fillStyle = `rgba(180, 200, 235, ${a.toFixed(3)})`;
    ctx.fillRect(x, y, 1, 1);
  }

  // Mid stars.
  for (let i = 0; i < 140; i++) {
    const x = rng() * STARFIELD_SIZE;
    const y = rng() * STARFIELD_SIZE;
    const r = rng() < 0.7 ? 1 : 1.5;
    const a = 0.35 + rng() * 0.4;
    ctx.fillStyle = `rgba(210, 225, 250, ${a.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Bright accent stars with halos. Slight colour variation to suggest
  // different stellar types.
  const accentTints = ['#FFFFFF', '#E0E8FF', '#FFE8C0', '#C0E4FF'];
  for (let i = 0; i < 25; i++) {
    const x = rng() * STARFIELD_SIZE;
    const y = rng() * STARFIELD_SIZE;
    const r = 1.2 + rng() * 1.2;
    const tint = accentTints[Math.floor(rng() * accentTints.length)];
    // Halo.
    const halo = ctx.createRadialGradient(x, y, 0, x, y, r * 4);
    halo.addColorStop(0, `${tint}99`);
    halo.addColorStop(1, `${tint}00`);
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(x, y, r * 4, 0, Math.PI * 2);
    ctx.fill();
    // Core.
    ctx.fillStyle = tint;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Get (or generate-and-cache) the main-menu key art — a 1920×1080
 * procedural nebula. Caller (`main-menu.ts`) draws this scaled to the
 * overlay's actual dimensions; the menu DOM stays unchanged on top.
 */
export function getMainMenuKeyArt(): OffscreenCanvas | null {
  if (keyArtCache !== undefined) return keyArtCache;
  if (typeof OffscreenCanvas === 'undefined') {
    keyArtCache = null;
    return null;
  }
  const canvas = new OffscreenCanvas(KEY_ART_W, KEY_ART_H);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    keyArtCache = null;
    return null;
  }
  paintMainMenuKeyArt(ctx, mulberry32(fnv1a('main-menu')));
  keyArtCache = canvas;
  return canvas;
}

/**
 * Paint the main-menu nebula. Layered radial gradients in deep blue /
 * violet / amber give the impression of a quiet stellar nursery.
 * Foreground star scatter + a couple of brighter "near" stars sit on
 * top so the title lockup has some structure to anchor against.
 */
function paintMainMenuKeyArt(
  ctx: OffscreenCanvasRenderingContext2D,
  rng: () => number,
): void {
  // Base — near-black with a barely-visible gradient suggesting depth.
  const base = ctx.createLinearGradient(0, 0, 0, KEY_ART_H);
  base.addColorStop(0, '#02030A');
  base.addColorStop(1, '#080514');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, KEY_ART_W, KEY_ART_H);

  // Large soft nebula blobs — palette-tinted to GRAPHICS palette.
  const blobs: Array<{ x: number; y: number; r: number; tint: string }> = [
    { x: KEY_ART_W * 0.25, y: KEY_ART_H * 0.55, r: 700, tint: 'rgba(60, 90, 200, 0.35)' },
    { x: KEY_ART_W * 0.7, y: KEY_ART_H * 0.35, r: 600, tint: 'rgba(160, 60, 180, 0.28)' },
    { x: KEY_ART_W * 0.55, y: KEY_ART_H * 0.75, r: 500, tint: 'rgba(220, 130, 60, 0.18)' },
    { x: KEY_ART_W * 0.1, y: KEY_ART_H * 0.15, r: 400, tint: 'rgba(50, 130, 180, 0.22)' },
    { x: KEY_ART_W * 0.9, y: KEY_ART_H * 0.85, r: 450, tint: 'rgba(90, 60, 140, 0.22)' },
  ];
  for (const b of blobs) {
    const g = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
    g.addColorStop(0, b.tint);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
    ctx.fill();
  }

  // A second pass of smaller, denser blobs to give the nebulae texture
  // rather than smooth-only gradients.
  for (let i = 0; i < 30; i++) {
    const cx = rng() * KEY_ART_W;
    const cy = rng() * KEY_ART_H;
    const r = 60 + rng() * 180;
    const hue = ['rgba(80, 100, 200, 0.10)', 'rgba(160, 80, 180, 0.10)', 'rgba(200, 130, 80, 0.06)'][
      Math.floor(rng() * 3)
    ];
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, hue);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Star scatter — three populations like the in-game starfield, scaled
  // up for the 1920×1080 canvas.
  for (let i = 0; i < 1500; i++) {
    const x = rng() * KEY_ART_W;
    const y = rng() * KEY_ART_H;
    const a = 0.1 + rng() * 0.2;
    ctx.fillStyle = `rgba(190, 210, 235, ${a.toFixed(3)})`;
    ctx.fillRect(x, y, 1, 1);
  }
  for (let i = 0; i < 350; i++) {
    const x = rng() * KEY_ART_W;
    const y = rng() * KEY_ART_H;
    const r = rng() < 0.7 ? 1 : 1.5;
    const a = 0.4 + rng() * 0.4;
    ctx.fillStyle = `rgba(220, 235, 255, ${a.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Bright accent stars with halos.
  const accentTints = ['#FFFFFF', '#E0E8FF', '#FFE8C0', '#C0E4FF'];
  for (let i = 0; i < 60; i++) {
    const x = rng() * KEY_ART_W;
    const y = rng() * KEY_ART_H;
    const r = 1.5 + rng() * 1.5;
    const tint = accentTints[Math.floor(rng() * accentTints.length)];
    const halo = ctx.createRadialGradient(x, y, 0, x, y, r * 5);
    halo.addColorStop(0, `${tint}99`);
    halo.addColorStop(1, `${tint}00`);
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(x, y, r * 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = tint;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Tile-blit a starfield bitmap across the given screen rect with parallax
 * offset. `parallaxOffsetX/Y` are the *screen-pixel* offsets the caller
 * derived from the current view's camera position; this function tiles
 * the bitmap so the visible rect is fully painted without seams.
 *
 * No-op if `bitmap` is null (headless test env).
 */
export function drawTiledStarfield(
  ctx: CanvasRenderingContext2D,
  bitmap: OffscreenCanvas | null,
  w: number,
  h: number,
  parallaxOffsetX: number,
  parallaxOffsetY: number,
): void {
  if (!bitmap) return;
  // Wrap offset into [0, STARFIELD_SIZE) so we always start from a
  // valid tile origin.
  const ox = ((parallaxOffsetX % STARFIELD_SIZE) + STARFIELD_SIZE) % STARFIELD_SIZE;
  const oy = ((parallaxOffsetY % STARFIELD_SIZE) + STARFIELD_SIZE) % STARFIELD_SIZE;
  const startX = -ox;
  const startY = -oy;
  for (let yy = startY; yy < h; yy += STARFIELD_SIZE) {
    for (let xx = startX; xx < w; xx += STARFIELD_SIZE) {
      ctx.drawImage(bitmap, xx, yy);
    }
  }
}
